/**
 * Keeping a hidden audio element in step with the visible multicam video.
 *
 * In the creator's multicam studio the visible <video> is the clock. When the
 * set's audio comes from a different angle than the one on screen, that
 * angle plays in a hidden element which follows the video: it plays and
 * pauses with it, and is re-seeked only when it has drifted noticeably.
 * Corrections are skipped while either element is seeking or buffering and
 * rate-limited afterwards; seeking a media element that is still settling
 * just restarts the settle, and correcting on every timeupdate made the
 * audio stutter ("seek thrash").
 */

/** How far the audio may drift from the video before it is re-seeked. */
export const AUDIO_DRIFT_TOLERANCE_S = 0.25;
/** Minimum time between two drift corrections. */
export const AUDIO_CORRECTION_COOLDOWN_MS = 1000;
/** Below this a forced re-sync doesn't seek: the jump would be audible and pointless. */
const FORCED_SEEK_THRESHOLD_S = 0.04;

// HTMLMediaElement.readyState values (spelled out so this runs without a DOM).
const HAVE_METADATA = 1;
const HAVE_FUTURE_DATA = 3;

export interface MediaSnapshot {
  paused: boolean;
  seeking: boolean;
  readyState: number;
  currentTime: number;
  /** The file's own length in seconds (NaN or missing while unknown). */
  duration?: number;
  ended?: boolean;
}

export interface AudioSyncPlan {
  /** Seek the audio here (seconds), or leave it where it is. */
  seekToS: number | null;
  play: boolean;
  pause: boolean;
}

export const snapshotMedia = (el: HTMLMediaElement): MediaSnapshot => ({
  paused: el.paused,
  seeking: el.seeking,
  readyState: el.readyState,
  currentTime: el.currentTime,
  duration: el.duration,
  ended: el.ended,
});

/**
 * What the audio element should do now.
 *
 * @param targetS where the audio should be, in seconds of its own file, or
 *   null when the audio angle has no footage at this point of the timeline
 *   (the honest result there is silence).
 * @param force a discrete event (seek finished, metadata loaded, the audio
 *   source changed): re-align now, ignoring the cooldown.
 */
export const planAudioSync = ({
  video,
  audio,
  targetS,
  force,
  nowMs,
  lastCorrectionAtMs,
}: {
  video: MediaSnapshot;
  audio: MediaSnapshot;
  targetS: number | null;
  force: boolean;
  nowMs: number;
  lastCorrectionAtMs: number;
}): AudioSyncPlan => {
  const plan: AudioSyncPlan = { seekToS: null, play: false, pause: false };

  // No footage here, or the file has already run out (a stored length can be
  // a little longer than the file): the honest result is silence.
  const audioLengthS =
    typeof audio.duration === 'number' && Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : Infinity;
  if (targetS === null || targetS >= audioLengthS) {
    plan.pause = !audio.paused;
    return plan;
  }

  const videoBuffering = video.seeking || video.readyState < HAVE_FUTURE_DATA;
  const audioCanSeek = audio.readyState >= HAVE_METADATA;
  const audioSettling = audio.seeking || (!audio.paused && audio.readyState < HAVE_FUTURE_DATA);
  const drift = Math.abs(audio.currentTime - targetS);

  if (audioCanSeek) {
    if (force) {
      if (drift > FORCED_SEEK_THRESHOLD_S) plan.seekToS = targetS;
    } else if (
      drift > AUDIO_DRIFT_TOLERANCE_S &&
      !videoBuffering &&
      !audioSettling &&
      nowMs - lastCorrectionAtMs >= AUDIO_CORRECTION_COOLDOWN_MS
    ) {
      plan.seekToS = targetS;
    }
  }

  // Audible only while the picture is actually moving: a stalled or seeking
  // video would otherwise leave the soundtrack running ahead of it.
  const shouldPlay = !video.paused && !videoBuffering;
  // play() on an ended element starts it again from the top; only a seek
  // back into the file may restart it.
  const wouldRestart = Boolean(audio.ended) && plan.seekToS === null;
  if (shouldPlay && audio.paused && !wouldRestart) plan.play = true;
  if (!shouldPlay && !audio.paused) plan.pause = true;
  return plan;
};
